import { ACCOUNT_DOMAINS, AccountType, accountDomainPermissions } from '@percent/domain/iam'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  MEMBER_PROFILE_VIEW_PERMISSIONS,
  MEMBER_TYPE_FOR_ACCOUNT_TYPE
} from '@percent/admin-dashboard/app/memberProfile/memberProfileAccess'
import { CanViewAccountType } from './accountSearch.utils'

/**
 * Shared FGA gating for the account search. Mirrors the AccessRoute guards on the
 * destination pages: the relevant permission is always required.
 *
 * `canSearch` decides whether the search surface (trigger + modal) is shown at all —
 * being able to list any one of the three account types is enough, since the search
 * spans all of them; `canViewType` then filters individual results so every one is actionable,
 * and `canViewTypeless` does the same for accounts with no type, which open on the general profile.
 * Listing alone therefore shows the trigger: a caller who can list but view nothing gets an
 * empty result set rather than a hidden search.
 */
export function useAccountSearchAccess(): {
  canSearch: boolean
  canViewType: CanViewAccountType
  canViewTypeless: boolean
} {
  const { userHasPermissions } = usePermissions()

  const canViewType: CanViewAccountType = (type: AccountType) =>
    userHasPermissions(MEMBER_PROFILE_VIEW_PERMISSIONS[MEMBER_TYPE_FOR_ACCOUNT_TYPE[type]])

  return {
    canSearch: userHasPermissions({ any: accountDomainPermissions(ACCOUNT_DOMAINS, 'can_list') }),
    canViewType,
    canViewTypeless: userHasPermissions(MEMBER_PROFILE_VIEW_PERMISSIONS.general)
  }
}
